import { change, DecoratedFormProps, InjectedFormProps, reduxForm } from 'redux-form';
import { FormError } from 'src/components/redux-form/FormError';
import React, { useState } from 'react';
import {
    emailList,
    maxLength,
    minLength,
    minPhoneLength,
    required,
    Validators,
} from 'src/components/redux-form/utils/validators';
import classNames from 'classnames';
import { FieldInput } from 'src/components/redux-form/fields/FieldInput';
import styles from '../assets/password-recovery-screen.module.scss';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dispatch } from 'redux';
import { toast } from 'react-toastify';
import { FieldDate } from '../../../../../components/redux-form/fields/FieldDate';
import { asyncPersistIntakeAbaIntake } from '../../../../../store/actions/intakeSent-actions';
import { push } from 'connected-react-router';
import Router from '../../../../../navigation/router';
import { routes } from '../../../../../navigation';
import { FieldSelectList } from '../../../../../components/redux-form/fields/FieldSelectList';
import GeneralEnum from '../../../../../enumerables/general-enum';
import { getValFromEnum } from '../../../../../components/redux-form/utils/dropdown-values';
import { FieldTextArea } from '../../../../../components/redux-form/fields/FieldTextArea';

export interface AbaIntakeFormData {
    student: string;
    formId: string;

    currentMainConcern: string;
    educationalHistory: string;
    abaExperience: string;
    currentTherapy: string;
    childStrength: string;
    possibleReinforcer: string;
    communications: [];
    communicationOther: string;
    behaviors: [];
    behaviorOther: string;
    interests: [];
    interestOther: string;
    socials: [];
    socialOther: string;
    schedule: [];
    specificService: string;

    isKeepingAppointment: boolean;
    isWhatFromUs: boolean;
    isWhatFromYou: boolean;
    isGeneral: boolean;
    isConfidentiality: boolean;
    specialProcedures: [];

    isInfoGathering: boolean;
    isAssessment: boolean;
    isProgramDevelopment: boolean;
    isDirectService: boolean;
    isContinuousProgressMonitoring: boolean;

    fullName: string;
    ssnFour: string;
    patientName: string;
    todayDate: string;
}

interface AbaIntakeFormProps {
    initialValues: any;
    setIsLoader: any;
    setIsOtherCommunication: any;
    isOtherCommunication: any;
    setIsOtherBehavior: any;
    isOtherBehavior: any;
    setIsOtherInterest: any;
    isOtherInterest: any;
    setIsOtherSocial: any;
    isOtherSocial: any;
}

type InjectedProps = InjectedFormProps<AbaIntakeFormData, AbaIntakeFormProps>;

const validators: Validators<AbaIntakeFormData> = {
    currentMainConcern: [required({ message: "It's required." })],
    educationalHistory: [required({ message: "It's required." })],
    abaExperience: [required({ message: "It's required." })],
    currentTherapy: [required({ message: "It's required." })],
    childStrength: [required({ message: "It's required." })],
    possibleReinforcer: [required({ message: "It's required." })],

    isKeepingAppointment: [required({ message: "It's required." })],
    isWhatFromUs: [required({ message: "It's required." })],
    isWhatFromYou: [required({ message: "It's required." })],
    isGeneral: [required({ message: "It's required." })],
    isConfidentiality: [required({ message: "It's required." })],
    specialProcedures: [required({ message: "It's required." })],

    isInfoGathering: [required({ message: "It's required." })],
    isAssessment: [required({ message: "It's required." })],
    isProgramDevelopment: [required({ message: "It's required." })],
    isDirectService: [required({ message: "It's required." })],
    isContinuousProgressMonitoring: [required({ message: "It's required." })],

    fullName: [required({ message: 'Full Name is required.' }), maxLength({ max: 255 })],
    patientName: [required({ message: "Patient' Name is required." }), maxLength({ max: 255 })],
    ssnFour: [required({ message: 'Last 4 Digits of the SSN is required.' }), minLength({ min: 4 })],
    todayDate: [required({ message: 'Date is required.' }), maxLength({ max: 255 })],
};

const AbaIntakeForm: React.FunctionComponent<InjectedProps & AbaIntakeFormProps> = ({
    handleSubmit,
    error,
    dirty,
    submitting,
    initialValues,
    setIsLoader,
    isOtherCommunication,
    setIsOtherCommunication,
    isOtherBehavior,
    setIsOtherBehavior,
    isOtherInterest,
    setIsOtherInterest,
    isOtherSocial,
    setIsOtherSocial,
}: InjectedProps & AbaIntakeFormProps) => {
    const communicationsValues: SelectObject[] = [
        { label: 'Delayed speech and language skills', value: 1 },
        { label: 'Repeats words or phrases over and over (echolalia)', value: 2 },
        { label: 'Reverse pronouns (e.g., says "you" instead of "I"', value: 3 },
        { label: 'Gives unrelated answers to questions', value: 4 },
        { label: 'Does not point or respond to pointing', value: 5 },
        { label: 'Uses few or no gestures (e.g., does not wave goodbye)', value: 6 },
        { label: 'Talks in a flat, robot-like, or sing-song voice', value: 7 },
        { label: 'Does not pretend play (e.g., does not pretend to "feed" a doll)', value: 8 },
        { label: 'Does not understand jokes, sarcasm, or teasing', value: 9 },
        { label: 'Other', value: 10 },
    ];
    const behaviorsValues: SelectObject[] = [
        { label: 'Hyperactivity (very active)', value: 1 },
        { label: 'Impulsivity (acting without thinking)', value: 2 },
        { label: 'Short attention span', value: 3 },
        { label: 'Aggression', value: 4 },
        { label: 'Causing self-injury', value: 5 },
        { label: 'Temper tantrums', value: 6 },
        { label: 'Unusual eating habits', value: 7 },
        { label: 'Unusual sleeping habits', value: 8 },
        { label: 'Unusual mood or emotional reactions', value: 9 },
        { label: 'Lack of fear or more fear than expected', value: 10 },
        { label: 'Unusual reactions to the way things sound, smell, taste, look, or feel', value: 11 },
        { label: 'Toileting issues', value: 12 },
        { label: 'Other', value: 13 },
    ];

    const interestsValues: SelectObject[] = [
        { label: 'Lines up toys or other objects', value: 1 },
        { label: 'Plays with toys the same way every time', value: 2 },
        { label: 'Likes parts of objects (e.g., wheels)', value: 3 },
        { label: 'Is very organized', value: 4 },
        { label: 'Gets upset by minor changes', value: 5 },
        { label: 'Has obsessive interests', value: 6 },
        { label: 'Has to follow certain routines', value: 7 },
        { label: 'Flaps hands, rocks body, or spins self in circles', value: 8 },
        { label: 'Other', value: 9 },
    ];

    const socialsValues: SelectObject[] = [
        { label: 'Avoids eye-contact', value: 1 },
        { label: 'Prefers to play alone', value: 2 },
        { label: 'Does not share interests with others', value: 3 },
        { label: 'Only interacts to achieve a desired goal', value: 4 },
        { label: 'Has flat or inappropriate facial expressions', value: 5 },
        { label: 'Does not understand personal space or boundaries', value: 6 },
        { label: 'Avoids or resists physical contact', value: 7 },
        { label: 'Is not comforted by others during distress', value: 8 },
        { label: "Has trouble understanding other people's feelings or talking about own feelings", value: 9 },
        { label: 'Other', value: 10 },
    ];

    const specialProcedures = GeneralEnum ? GeneralEnum.getEnum('IntakeAbaSpecialProcedures') : {};
    const specialProceduresValues: SelectObject[] = getValFromEnum(specialProcedures);

    specialProceduresValues.forEach((sP: { label: string; value: number | string }, p: number) => {
        if (sP.label == 'CANCEL_POLICY') {
            sP.label =
                '48-Hour Cancellation Policy - For all sessions, there is a 48-hour cancellation policy, barring emergencies or last-minute sickness. Notice should be emailed directly to the provider, aba@manhattanpsychologygroup.com or given by telephone at 646-450-6210. If 48-hours notice is not given, the full fee will be charged.';
        }
        if (sP.label == 'PAYMENT_INSURANCE') {
            sP.label =
                'Payment & Insurance - Payment is due in full at the time of service or in advance in the form of cash, check or credit card, made payable to “MANHATTAN PSYCHOLOGY GROUP, PC.” If there is a large number of treatment hours per week, we will email you a weekly balance due email outlining the services rendered and how to make payment. A monthly billing statement can be provided to submit for possible out-of-network reimbursement. We can also provide this receipt weekly upon request. MPG is not in network with insurance. We will do our best to provide accurate CPT codes. However, it is the patient’s responsibility to be aware of reimbursement policies from their own insurance company.';
        }
        if (sP.label == 'NOT_PAYMENT') {
            sP.label =
                'Late & Non-Payment - MPG will charge your credit card on file for any services rendered, missed sessions, or any last-minute cancellations that are not paid within 5 business days of the scheduled time of service. There is a 3.5% administrative fee via PayPal. Non-payment may result in legal action and incur additional fees.';
        }
        if (sP.label == 'PAYMENT_FOR_NON_DIRECT') {
            sP.label =
                'Payment for Non-Direct Patient Care Services - Telephone and email consultation, supervision, report writing, observations, behavior plans, documentation requests, travel time, court-related requests, and all other non-direct patient care requests are billed at the stated hourly rate, in 15 intervals of the provider’s billable rate. We will always try our best to notify you in advance of any anticipated billable time outside of session. There may not be CPT codes for these services, which means that your insurance company will most likely not reimburse you. Again, it is the patient’s responsibility to check with their insurance regarding reimbursement policies.';
        }
        if (sP.label == 'SPECIAL_PROCEDURE_FOR_DOE') {
            sP.label =
                'Special Procedures for DOE Funded Patients/Clients (Skip if you are private pay) When the DOE is paying for services, it is critical that we complete and mail out the vendor  invoice on the first of each month. Since we cannot mail out the invoice without the parent’s  signature, we may ask parents to sign the invoice in advance to prevent any delays. In such  instances, we will email a picture of the completed invoice to the parent for final approval.';
        }
        if (sP.label == 'EMERGENCIES') {
            sP.label =
                'Emergencies - In the event of an emergency, patients/parents should call 911 and/or go to their nearest  emergency room. Patients should not contact MPG regarding an emergency, as MPG does  not provide 24-hour patient coverage.';
        }
        if (sP.label == 'STOP_TREATMENT') {
            sP.label =
                'Stopping Treatment - Sometimes, despite everyone’s best effort, it is not a good fit, and either party must terminate  treatment. We will need to stop treatment if there are repeated issues with changing the schedule,  cancelling sessions, ending sessions early, starting sessions late and/or interpersonal difficulties  that we believe are interfering with our ability to perform ABA. Of course, we will try our best to  resolve any issues and give notice when we are concerned about treatment fidelity. We will work  with families to develop an individualized plan. If we are not able to reconcile important issues  in treatment planning and delivery, we will try to give at least 2-4 weeks notice so that you/we  can find replacement providers. Likewise, if you are unhappy with our treatment and want to  terminate, we ask that you give at least 2-4 weeks notice.';
        }
        if (sP.label == 'DOES_NOT_APPLY_TO_ME') {
            sP.label = 'Does not apply to me (DOE funded)';
        }
    });

    const scheduleDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const scheduleTime = ['Morning', 'Afternoon', 'Evening', 'N/A'];

    const renderSchedule = () => {
        return scheduleTime.forEach((t: string) => {
            return (
                <FieldInput
                    name={'schedule[' + t + ']'}
                    type="checkbox"
                    wrapperConfig={{
                        label: '',
                    }}
                />
            );
        });
    };

    return (
        <form onSubmit={handleSubmit} className={classNames(styles.form)}>
            <h2>ABA Intake Form</h2>
            <h3 className={classNames(styles.subTitle)}>Brief Background Information</h3>
            <div className={classNames(styles.fixNotice)}>
                Brief Background Information (this will be expanded upon during the parent intake)
            </div>
            <FormError error={error} />
            <FieldInput
                name="student"
                type="hidden"
                wrapperConfig={{
                    label: '',
                }}
            />
            <FieldInput
                name="formId"
                type="hidden"
                value={3}
                wrapperConfig={{
                    label: '',
                }}
            />
            <FieldTextArea
                name="currentMainConcern"
                //type="text"
                validate={validators.currentMainConcern}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label: 'What are your current main concerns (social, communication, academic, behavioral)? *',
                }}
            />
            <FieldTextArea
                name="educationalHistory"
                //type="text"
                validate={validators.educationalHistory}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label: "Please describe the patients' educational history. *",
                }}
            />
            <FieldTextArea
                name="abaExperience"
                //type="text"
                validate={validators.abaExperience}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label: 'Please describe any previous ABA experiences (positive and/or negative). *',
                }}
            />
            <FieldTextArea
                name="currentTherapy"
                //type="text"
                validate={validators.currentTherapy}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label:
                        'Please list all current therapies (OT, PT, Speech, etc.), including frequency and length of sessions.*',
                }}
            />
            <FieldTextArea
                name="childStrength"
                //type="text"
                validate={validators.childStrength}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label:
                        "Please describe your child's strengths (hobbies, skills, socially, level of communication, academic interests, etc.). *",
                }}
            />
            <FieldTextArea
                name="possibleReinforcer"
                //type="text"
                validate={validators.possibleReinforcer}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label: 'What are some possible reinforcers (food, toys, physical contact, activities, other)? *',
                }}
            />

            <label htmlFor="communications">
                Communication: Please check off all of the symptoms and behaviors that apply to your child
            </label>
            <FieldSelectList
                multiple={true}
                name="communications"
                data={communicationsValues}
                textField={'label'}
                valueField={'value'}
                onChange={(e: any) => {
                    if (e) {
                        const other = e.filter((it: { label: string; value: number }) =>
                            it.label == 'Other' ? it.value : 0,
                        );
                        if (other && other[0] !== undefined) {
                            setIsOtherCommunication(true);
                        } else {
                            setIsOtherCommunication(false);
                        }
                    }
                }}
            />
            <FieldInput
                name="communicationOther"
                type={isOtherCommunication ? 'text' : 'hidden'}
                validate={validators.communicationOther}
                wrapperConfig={{
                    label: '',
                }}
            />

            <label htmlFor="behaviors">
                Other behaviors: Please check off all of the symptoms and behaviors that apply to your child
            </label>
            <FieldSelectList
                multiple={true}
                name="behaviors"
                data={behaviorsValues}
                textField={'label'}
                valueField={'value'}
                onChange={(e: any) => {
                    if (e) {
                        const other = e.filter((it: { label: string; value: number }) =>
                            it.label == 'Other' ? it.value : 0,
                        );
                        if (other && other[0] !== undefined) {
                            setIsOtherBehavior(true);
                        } else {
                            setIsOtherBehavior(false);
                        }
                    }
                }}
            />
            <FieldInput
                name="behaviorOther"
                type={isOtherBehavior ? 'text' : 'hidden'}
                validate={validators.behaviorOther}
                wrapperConfig={{
                    label: '',
                }}
            />

            <label htmlFor="interests">Unusual Interests: Please check off all of the symptoms and behaviors that apply to your child</label>
            <FieldSelectList
                multiple={true}
                name="interests"
                data={interestsValues}
                textField={'label'}
                valueField={'value'}
                onChange={(e: any) => {
                    if (e) {
                        const other = e.filter((it: { label: string; value: number }) =>
                            it.label == 'Other' ? it.value : 0,
                        );
                        if (other && other[0] !== undefined) {
                            setIsOtherInterest(true);
                        } else {
                            setIsOtherInterest(false);
                        }
                    }
                }}
            />
            <FieldInput
                name="interestOther"
                type={isOtherInterest ? 'text' : 'hidden'}
                validate={validators.interestOther}
                wrapperConfig={{
                    label: '',
                }}
            />

            <label htmlFor="socials">
                Social: Please check off all of the symptoms and behaviors that apply to your child
            </label>
            <FieldSelectList
                multiple={true}
                name="socials"
                data={socialsValues}
                textField={'label'}
                valueField={'value'}
                onChange={(e: any) => {
                    if (e) {
                        const other = e.filter((it: { label: string; value: number }) =>
                            it.label == 'Other' ? it.value : 0,
                        );
                        if (other && other[0] !== undefined) {
                            setIsOtherSocial(true);
                        } else {
                            setIsOtherSocial(false);
                        }
                    }
                }}
            />
            <FieldInput
                name="socialOther"
                type={isOtherSocial ? 'text' : 'hidden'}
                validate={validators.socialOther}
                wrapperConfig={{
                    label: '',
                }}
            />

            <div className={classNames(styles.schedule)}>
                <div>
                    What is your child's current educational and treatment schedule, including ABA and related services
                    (speech, OT, PT, tutoring, etc.)?
                </div>
                <div className={classNames(styles.scheduleItemsList)}>
                    <div className={classNames(styles.scheduleTime)}>
                        <div className={classNames(styles.scheduleDay, styles.scheduleDayLabel)}></div>
                        <div className={classNames(styles.scheduleDay)}>M</div>
                        <div className={classNames(styles.scheduleDay)}>T</div>
                        <div className={classNames(styles.scheduleDay)}>W</div>
                        <div className={classNames(styles.scheduleDay)}>T</div>
                        <div className={classNames(styles.scheduleDay)}>F</div>
                        <div className={classNames(styles.scheduleDay)}>S</div>
                        <div className={classNames(styles.scheduleDay)}>S</div>
                    </div>
                    {scheduleTime.map((t: string, i: number) => {
                        return (
                            <div className={classNames(styles.scheduleTime)} key={'time' + i}>
                                <div className={classNames(styles.scheduleDay, styles.scheduleDayLabel)}>{t}</div>
                                {scheduleDays.map((d: string, j: number) => {
                                    return (
                                        <div key={'day_sc' + j} className={classNames(styles.scheduleDay)}>
                                            <FieldInput
                                                name={'schedule[' + t + '][' + d + ']'}
                                                type="checkbox"
                                                wrapperConfig={{
                                                    classes: {
                                                        inputWrapper:
                                                            'customCheckbox ' + classNames(styles.fixCheckbox),
                                                    },
                                                }}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </div>

            <FieldTextArea
                name="specificService"
                validate={validators.specificService}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label:
                        'Please elaborate on above question to include specific  service and location of service if not at home',
                }}
            />

            <h3 className={classNames(styles.subTitle)}>Informed Consent & Expectations for Working Together</h3>
            <div className={classNames(styles.fixNotice)}>
                Please check each section and print your name at the bottom and provide the last 4 digits of your SSN
            </div>

            <div className={classNames(styles.checkBoxContainer)}>
                <div>
                    <b>Keeping and Cancelling Appointments </b>
                </div>
                <label htmlFor="isKeepingAppointment" className={classNames(styles.checkBoxLabel)}>
                    It is very important for treatment to be successful that we maintain a treatment schedule that is consistent and mutually attainable. Our providers will be dedicating many hours each week to  your family to provide direct service ABA and ABA programming to ensure treatment is  delivered consistently and comprehensively. We will set the schedule each month and ask you to confirm it in writing so there are no misunderstandings. We also ask that you be on time for your  sessions so there are no delays. Because of the large number of hours allotted each week and  complexity of setting the schedule in advance, if you need to change the schedule, we ask that you give us at least 1-week notice. Please keep in mind that therapists may able to attend  community outings if applicable. Our expectation is that all the scheduled hours will be  maintained, while the treatment setting may change. When there is sickness or last-minute  emergencies and you need to cancel, we ask that you notify us (emailing the provider directly,  calling their cell phone, and/or calling our main telephone 646-450-6210) as soon as possible to  avoid any unnecessary commuting.
                </label>
                <FieldInput
                    name="isKeepingAppointment"
                    type="checkbox"
                    validate={validators.isKeepingAppointment}
                    wrapperConfig={{
                        label: 'Please check it to approve the above *',
                        classes: {
                            inputWrapper: 'customCheckbox customCheckboxWide ',
                        },
                    }}
                />
            </div>

            <div className={classNames(styles.checkBoxContainer)}>
                <div>
                    <b>What To Expect from Us</b>
                </div>
                <label htmlFor="isWhatFromUs" className={classNames(styles.checkBoxLabel)}>
                    We strive to deliver the best possible care in several ways. First, our staff are highly trained,  educated and experienced in ABA. Second, we will always try our best to be on-time to sessions  and keep to the planned scheduled, baring unforeseen issues with public transportation which can  be unreliable at times. If we need to change or cancel a session, we will give as much notice as  possible. Third, we will always communicate professionally and respectfully, being mindful of  different cultures and traditions. If you ever have any issues with your provider, we ask that you  address it with them directly first. If that does not resolve the problem, you can always speak  with our director of ABA services. It is also important to acknowledge that therapy involves  potential physical, emotional, and mental risks, including but not limited to the potential for  property damage, personal injury, and emotional duress. Proper implementation of Applied  Behavior Analysis requires ongoing training and support from a Certified Behavior Analyst,  adherence to the treatment plan, and diligence in data collection.
                </label>
                <FieldInput
                    name="isWhatFromUs"
                    type="checkbox"
                    validate={validators.isWhatFromUs}
                    wrapperConfig={{
                        label: 'Please check it to approve the above *',
                        classes: {
                            inputWrapper: 'customCheckbox customCheckboxWide ',
                        },
                    }}
                />
            </div>

            <div className={classNames(styles.checkBoxContainer)}>
                <div>
                    <b>What We Expect from You</b>
                </div>
                <label htmlFor="isWhatFromYou" className={classNames(styles.checkBoxLabel)}>
                    We expect that you will be on time to sessions, keep to the agreed schedule, give at least 1-week  notice if you need to change the treatment setting, and let us know ASAP if you need to cancel  due to sickness. We expect you to be courteous and communicate respectfully with our staff.  Finally, we expect you to participate in treatment consistently to the best of your ability in a way  that supports the patient and his/her treatment goals. You also understand that there are no  guarantees that treatment will be successful.
                </label>
                <FieldInput
                    name="isWhatFromYou"
                    type="checkbox"
                    validate={validators.isWhatFromYou}
                    wrapperConfig={{
                        label: 'Please check it to approve the above *',
                        classes: {
                            inputWrapper: 'customCheckbox customCheckboxWide ',
                        },
                    }}
                />
            </div>

            <div className={classNames(styles.checkBoxContainer)}>
                <div>
                    <b>General & Electronic Communication </b>
                </div>
                <label htmlFor="isGeneral" className={classNames(styles.checkBoxLabel)}>
                    We ask that patients/clients email or call us. Please do not text with our staff or providers. MPG  takes reasonable precautions in following Health Information Privacy recommendations per the Department of Health & Human Services in the use of unencrypted email to communicate with patients, such as sending receipts, scheduling appointments and coordinating care with collateral providers.
                </label>
                <FieldInput
                    name="isGeneral"
                    type="checkbox"
                    validate={validators.isGeneral}
                    wrapperConfig={{
                        label: 'Please check it to approve the above *',
                        classes: {
                            inputWrapper: 'customCheckbox customCheckboxWide ',
                        },
                    }}
                />
            </div>

            <div className={classNames(styles.checkBoxContainer)}>
                <div>
                    <b>Confidentiality & Sharing Information </b>
                </div>
                <label htmlFor="isConfidentiality" className={classNames(styles.checkBoxLabel)}>
                    Treatment from MPG is confidential and requests to share patient health information with those outside of MPG require written release. Written release is not required, however, to share patient  information internally between MPG staff. A request in writing can be given that information not be shared within MPG. MPG is obligated by law to break confidentiality if the patient is a danger to themselves or others, under court order or when there is suspicion of child abuse. Further,  certain information may be disclosed in instances of non-payment.
                </label>
                <FieldInput
                    name="isConfidentiality"
                    type="checkbox"
                    validate={validators.isConfidentiality}
                    wrapperConfig={{
                        label: 'Please check it to approve the above *',
                        classes: {
                            inputWrapper: 'customCheckbox customCheckboxWide ',
                        },
                    }}
                />
            </div>

            <label htmlFor="specialProcedures">Special Procedures for Private Pay Patients/ Clients (Check last box if you are DOE funded) *</label>
            <FieldSelectList
                multiple={true}
                name="specialProcedures"
                validate={validators.specialProcedures}
                data={specialProceduresValues}
                textField={'label'}
                valueField={'value'}
            />

            <h3 className={classNames(styles.subTitle)}>ABA Treatment Process (How We Work)</h3>
            <div className={classNames(styles.fixNotice)}>
                We structure our ABA programs in the following way to ensure the best possible outcomes based on
                training, experience and professional standards. All parts of ABA treatment listed below are equally
                important.
            </div>

            <div className={classNames(styles.checkBoxLabel)}>
                <b>Please check to verify you read each section below </b>
            </div>

            <div className={classNames(styles.checkBoxContainer)}>
                <div className={classNames(styles.checkBoxLabel)}>
                    1. Information Gathering: The beginning of treatment always begins with information gathering and assessments. Please email us all reports or evaluations you have, such as neuropsychological, speech and language, occupational therapy, and IEP. It would also be helpful if we could speak with any current or prior treatment providers should we have clinical questions, so please make sure to complete the attached release of information form.
                </div>
                <FieldInput
                    name="isInfoGathering"
                    type="checkbox"
                    validate={validators.isInfoGathering}
                    wrapperConfig={{
                        label: 'Please check it to approve the above *',
                        classes: {
                            inputWrapper: 'customCheckbox customCheckboxWide ',
                        },
                    }}
                />
            </div>

            <div className={classNames(styles.checkBoxContainer)}>
                <div className={classNames(styles.checkBoxLabel)}>
                    2. Assessment: The very first meeting will be with the parent(s) alone so that we can gather more detailed information and history towards creating the treatment goals. Next, we will meet with the patient/client to begin to assess strengths, areas for growth and possible treatment goals.
                </div>
                <FieldInput
                    name="isAssessment"
                    type="checkbox"
                    validate={validators.isAssessment}
                    wrapperConfig={{
                        label: 'Please check it to approve the above *',
                        classes: {
                            inputWrapper: 'customCheckbox customCheckboxWide ',
                        },
                    }}
                />
            </div>

            <div className={classNames(styles.checkBoxContainer)}>
                <div className={classNames(styles.checkBoxLabel)}>
                    3. Program Development: Once your provider has enough information, he/she will begin to create a program manual for treatment planning, activities scheduling, data collection and progress monitoring. This may also include formal skills assessments. During this time, he/she will also be pairing/bonding with the patient to build the therapeutic alliance and gain instructional control. This usually takes several weeks depending on the number of hours allotted per week.
                </div>
                <FieldInput
                    name="isProgramDevelopment"
                    type="checkbox"
                    validate={validators.isProgramDevelopment}
                    wrapperConfig={{
                        label: 'Please check it to approve the above *',
                        classes: {
                            inputWrapper: 'customCheckbox customCheckboxWide ',
                        },
                    }}
                />
            </div>

            <div className={classNames(styles.checkBoxContainer)}>
                <div className={classNames(styles.checkBoxLabel)}>
                    4. Direct Service – Once the goals and reinforcers are clearly identified, the provider(s) will begin to target increasing adaptive skills and reducing maladaptive skills, as well as any academic, social-emotional, or independent living skills. Direct service hours are usually done in 2-4 hour blocks of time to allow for better skill acquisition, and skills are taught across settings and providers to improve generalization. If the child is not in school and we are providing ABA in the home, we ask for parents to budget time for community outings and such.
                </div>
                <FieldInput
                    name="isDirectService"
                    type="checkbox"
                    validate={validators.isDirectService}
                    wrapperConfig={{
                        label: 'Please check it to approve the above *',
                        classes: {
                            inputWrapper: 'customCheckbox customCheckboxWide ',
                        },
                    }}
                />
            </div>

            <div className={classNames(styles.checkBoxContainer)}>
                <div className={classNames(styles.checkBoxLabel)}>
                    5. Continuous Progress Monitoring – In addition to direct service, providers are spending many required hours each week updating the program manual, analyzing data, writing reports and collaborating with collateral providers (both in and outside of MPG). These hours are vital to any successful ABA program.
                </div>
                <FieldInput
                    name="isContinuousProgressMonitoring"
                    type="checkbox"
                    validate={validators.isContinuousProgressMonitoring}
                    wrapperConfig={{
                        label: 'Please check it to approve the above *',
                        classes: {
                            inputWrapper: 'customCheckbox customCheckboxWide ',
                        },
                    }}
                />
            </div>

            <div className={classNames(styles.textBox)}>
                By typing my full name below and last 4 digits of my SSN, I verify that I have read and understand the
                terms of treatment with MPG outlined above and in the attached welcome packet.
            </div>

            <FieldInput
                name="fullName"
                type="text"
                validate={validators.fullName}
                wrapperConfig={{
                    label: 'Full Name: *',
                }}
            />
            <FieldInput
                name="ssnFour"
                type="text"
                validate={validators.ssnFour}
                wrapperConfig={{
                    label: 'Last 4 Digits of the SSN *',
                }}
            />
            <FieldInput
                name="patientName"
                type="text"
                validate={validators.patientName}
                wrapperConfig={{
                    label: 'Patient Name: *',
                }}
            />
            <FieldDate
                name="todayDate"
                type="text"
                date={true}
                validate={validators.todayDate}
                wrapperConfig={{
                    label: "Today's date *",
                }}
            />
            <div className={classNames(styles.actions)}>
                <button
                    type="submit"
                    className={classNames('btn', 'btn-success', 'mr-3')}
                    disabled={!dirty || submitting}
                >
                    <FontAwesomeIcon icon={faSave} /> Confirm and Send
                </button>
            </div>
        </form>
    );
};

export default reduxForm<AbaIntakeFormData, AbaIntakeFormProps>({
    form: 'aba-intake-form',
    enableReinitialize: true,
    onSubmit: async (data: AbaIntakeFormData, dispatch: Dispatch<any>, props: AbaIntakeFormProps) => {
        props.setIsLoader(true);
        await asyncPersistIntakeAbaIntake({ ...data }, dispatch);
    },
    onChange: async (
        values: Partial<AbaIntakeFormData>,
        dispatch: Dispatch<any>,
        props: any,
        previousValues: Partial<AbaIntakeFormData>,
    ) => {},
    onSubmitSuccess(
        result: any,
        dispatch: Dispatch<any>,
        props: DecoratedFormProps<AbaIntakeFormData, AbaIntakeFormProps, string>,
    ) {
        props.setIsLoader(false);
        //toast.success(`The form was successfully sent.`);
        dispatch(push(Router.generate(routes.INTAKE_PARENT_FORM_SUCCESS, {})));
    },

    onSubmitFail(errors: { _error: string }) {
        // props.setIsLoader(false);
        if (errors) {
            let field: any = null;
            Object.keys(errors).map((e: any, index: number, array: any) => {
                if (!field) {
                    field = e;
                    return;
                }
                return;
            });
            if (field) {
                const section = document.querySelector(`label[for='${field}']`);
                section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
        if (errors._error) {
            toast.error(errors._error);
        }
    },
})(AbaIntakeForm);
